import React, { useState, useEffect } from 'react';
import { Button, Col, Input, Row, Label, ModalBody, Form, TabPane, TabContent, Spinner, FormFeedback } from 'reactstrap';
import * as Yup from 'yup';
import { useFormik } from 'formik';
import { toast } from 'react-toastify';
import { useDispatch, useSelector } from 'react-redux';
import { getEstablishment, getWarehouseItemTransferList, updateStatement, updateWarehouseTransfer, uploadFoldersFile } from '../../../../../slices/thunks';
import { createSelector } from 'reselect';
import { setRequireCloseModal } from '../../../../../slices/statement/reducer';
import useFolderPaths from '../../../../../Components/Hooks/useFolderPathsHooks';
import useFileType from '../../../../../Components/Hooks/useFileTypeHooks';
import { DatePickerInput, DatesProvider } from '@mantine/dates';
import OInput from '../../../../../Components/OElements/OInput';

const ChargeDocumentForm = ({ data, isEdit, handleToggleModal, isOpen, activeTab, toggleTab }) => {
    const dispatch = useDispatch();
    const [isDisabled, setIsDisabled] = useState(false);

    const selectorStatements = createSelector(
        state => state.WarehouseItemTransfers,
        statement => ({
            isUpdating: statement.isUpdating,
            isUpdated: statement.isUpdated,
            isUpdateError: statement.isUpdateError,
            requireCloseModal: statement.requireCloseModal
        })
    );

    const { isUpdating, requireCloseModal } = useSelector(selectorStatements);
    const { file_type } = useFileType('GD');

    const uploadDocument = async (file, folderPaths, fileTypeId, establishmentId) => {
        if (!(file instanceof File)) {
            throw new Error("El archivo seleccionado no es válido.");
        }

        const formData = new FormData();
        formData.append("file", file);
        formData.append("file_type_id", fileTypeId);
        formData.append("establishment_id", establishmentId);

        folderPaths.forEach(path => formData.append("folder_paths", path));

        try {
            const response = await dispatch(uploadFoldersFile(formData)).unwrap();
            return response;
        } catch (error) {
            console.error("Error uploading file:", error);
            throw error;
        }
    };

    const validation = useFormik({
        initialValues: {
            document_num: null,
            document: '',
        },
        validationSchema: Yup.object({
            document_num: Yup.string().required('Campo requerido'),
            document: Yup.mixed()
                .required('El documento de guía de despacho es requerido')
                .test('documentFormat', 'Solo puede subir extensiones .pdf', value => {
                    if (value) {
                        const supportedFormats = ['pdf'];
                        return supportedFormats.includes(value.name.split('.').pop());
                    }
                    return true;
                })
                .test('documentSize', 'El archivo no puede superar el tamaño máximo de 10MB', value => {
                    if (value) {
                        return value.size <= 10485760;
                    }
                    return true;
                })
        }),
        onSubmit: async (values) => {
            setIsDisabled(true);

            const formData = {
                ...data.warehouse_transfer,
                document_number: values.document_num,
            }

            let dataEstablishment = null;

            try{
                dataEstablishment = await dispatch(getEstablishment(data.warehouse_transfer.establishment_id)).unwrap()
                console.log('dataEstablishment', dataEstablishment)
            }catch(error){
                toast.error('Error, no se ha logrado obtener la información del establecimiento: ' + error)
                setIsDisabled(false);
                return;
            }

            try{
                let documents = null;
                const folderPath = (
                    '/' + 
                    dataEstablishment.establishment_type.name +
                    '/' +
                    (
                        dataEstablishment.code_siep
                            ? dataEstablishment.code_siep + ' - '
                            : ''
                    ) + 
                    dataEstablishment.name + 
                    '/Abastecimiento/EPP/Transferencias'
                );
                documents = await uploadDocument(validation.values.document, [folderPath], file_type._id, dataEstablishment._id);
                const document = documents[documents.length - 1];
                if (document?._id) {
                    formData.document_id = document._id;
                }
            }catch(error){
                toast.error('Error, no se ha logrado cargar el documento: ' + error)
                setIsDisabled(false);
                return;
            }


            try{
                await dispatch(updateWarehouseTransfer(formData))
                await dispatch(getWarehouseItemTransferList({
                    establishment_id: dataEstablishment._id,
                    warehouse_usage_type_code: 'EPP',
                    all: true
                }));
                setIsDisabled(false);
                handleToggleModal();
            }catch(error){
                toast.error('Error, no se ha logrado vincular el documento a la transferencia: ' + error)
                setIsDisabled(false);
                return;
            }
        },
    });

    useEffect(() => {
        if (!isUpdating) {
            setIsDisabled(false);
        }
    }, [isUpdating]);

    useEffect(() => {
        if (isOpen) {
            if (requireCloseModal) {
                dispatch(setRequireCloseModal(false));
                handleToggleModal();
            }
        }
    }, [requireCloseModal]);

    return (
        <Form
            className='tablelist-form'
            onSubmit={e => {
                e.preventDefault();
                if (Object.keys(validation.errors).length != 0) { toast.error('Por favor, revisa la información ingresada. Hemos encontrado algunos detalles que necesitan corrección antes de continuar.', { autoClose: 4000 }); }
                validation.handleSubmit();
                return false;
            }}
        >
            <ModalBody className='modal-body'>
                <TabContent activeTab={'1'}>
                    <TabPane tabId='1'>
                        <Row className='g-3'>
                            <Col lg={4} >
                                <OInput
                                    name='document_num'
                                    label='Nro. Guía de Despacho'
                                    type='text'
                                    validation={validation}
                                    isRequired={true}
                                    classNameLabel='form-label'
                                    classNameInput='form-control'
                                />
                            </Col>
                            <Col lg={8}>
                                <Label for="document">Guía de Despacho <span style={{ color: 'red' }}>*</span></Label>
                                <Input
                                    name='document'
                                    id='document'
                                    className='form-control'
                                    placeholder='Ingrese el documento'
                                    type='file'
                                    accept='.pdf'
                                    validate={{ required: { value: true } }}
                                    onChange={(e) => {
                                        const file = e.currentTarget.files[0];
                                        validation.setFieldValue("document", file || '');
                                    }}
                                    onBlur={validation.handleBlur}
                                    invalid={validation.touched.document && validation.errors.document ? true : false}
                                />
                                {validation.touched.document &&
                                    validation.errors.document ? (
                                    <FormFeedback type='invalid'>
                                        {validation.errors.document}
                                    </FormFeedback>
                                ) : null}
                            </Col>
                        </Row>
                    </TabPane>
                </TabContent>
            </ModalBody>
            <div className='modal-footer'>
                <div className='hstack gap-2 justify-content-end'>
                    <Button
                        type='button'
                        onClick={() => {
                            handleToggleModal();
                        }} className='btn-danger btn-label'>
                        <i className="icon-x label-icon"></i>
                        Cancelar
                    </Button>
                    <button
                        type='submit'
                        className='btn btn-label btn-primary'
                        id='add-btn'
                        disabled={isDisabled}
                    >
                        {isDisabled ? (
                            <i className='label-icon'><Spinner size='sm' /></i>
                        ) : <i className={`${Icons.check2} label-icon`}></i>}
                        Cargar
                    </button>
                </div>
            </div>
        </Form>
    );
};

export default ChargeDocumentForm;
